import type {
  Service,
  Booking,
  Instructor,
  BookedServiceDetailModel,
  CreateBookingPreset,
  ParticipantBasicInfo,
} from "./types";
import { parseISO, format, isPast } from "date-fns";
import {
  type AdminServiceParticipantPaymentStatus,
  BookingCancellationSource,
  ServiceTypeEnum,
} from "@/api/model";

import { getFormattedCurrency } from "@/shared/utils/helpers";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

const dayIndexToStringMap: Record<number, string> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

const dayIndexToUpperCaseNameMap: Record<number, string> = {
  0: "SUNDAY",
  1: "MONDAY",
  2: "TUESDAY",
  3: "WEDNESDAY",
  4: "THURSDAY",
  5: "FRIDAY",
  6: "SATURDAY",
};

export const displayDays = ["S", "M", "T", "W", "T", "F", "S"];

export const getDayClass = (
  dayIndex: number,
  availabilityRules: any[]
): string => {
  let isAvailable = false;
  const currentDayString = dayIndexToStringMap[dayIndex];
  const currentDayUpperCase = dayIndexToUpperCaseNameMap[dayIndex];

  if (!availabilityRules || availabilityRules.length === 0) {
    return "text-gray-400";
  }

  const weeklyAvailabilityRule = availabilityRules.find((rule) =>
    Array.isArray(rule.weekly_availability)
  );

  if (weeklyAvailabilityRule) {
    if (
      weeklyAvailabilityRule.weekly_availability.includes(currentDayUpperCase)
    ) {
      isAvailable = true;
    }
  } else {
    for (const rule of availabilityRules) {
      const ruleType = (
        rule?.availability_type ||
        rule?.dayOfWeekType ||
        ""
      ).toLowerCase();

      switch (ruleType) {
        case "everyday":
          isAvailable = true;
          break;
        case "weekdays":
          if (dayIndex >= 1 && dayIndex <= 5) {
            isAvailable = true;
          }
          break;
        case "weekend":
          if (dayIndex === 0 || dayIndex === 6) {
            isAvailable = true;
          }
          break;
        case "sunday":
        case "monday":
        case "tuesday":
        case "wednesday":
        case "thursday":
        case "friday":
        case "saturday":
          if (ruleType === currentDayString) {
            isAvailable = true;
          }
          break;
        case "single_day":
          if (rule.specificDay === dayIndex) {
            isAvailable = true;
          }
          break;
      }
      if (isAvailable) break;
    }
  }
  return isAvailable ? "bg-gray-200 font-medium" : "text-gray-400";
};

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Constructs the BookedServiceDetailModel from raw data components.
 * @param bookedServiceId The original ID used for lookup.
 * @param service The found Service object.
 * @param rateId The specific rate ID associated with this booked session instance.
 * @param instructor The found Instructor object (can be null/undefined).
 * @param matchingBookings An array of Booking objects for this specific session.
 * @returns A BookedServiceDetailModel object.
 * @throws Error if matchingBookings array is empty.
 */
export function constructBookedServiceDetail(
  bookedServiceId: string,
  service: Service,
  rateId: string,
  instructor: Instructor | undefined,
  matchingBookings: Booking[]
): BookedServiceDetailModel {
  if (matchingBookings.length === 0) {
    throw new Error(
      "Cannot construct detail model with zero matching bookings."
    );
  }
  const representativeBooking = matchingBookings[0];
  const startTime = parseISO(representativeBooking.startTime);
  const endTime = parseISO(representativeBooking.endTime);

  const displayTimeRange = `${format(startTime, "h:mmaaa")} - ${format(endTime, "h:mmaaa")}`;

  const allCancelled = matchingBookings.every((b) => b.cancelledAt);
  const sessionIsPast = isPast(endTime);
  let status: "Active" | "Completed" | "Canceled" = "Active";
  if (allCancelled) {
    status = "Canceled";
  } else if (sessionIsPast) {
    status = "Completed";
  }

  const detail: BookedServiceDetailModel = {
    serviceId: service.id,
    serviceTitle: service.title,
    serviceDescription: service.description,
    isGroupService: service.isGroupService,
    maxParticipants: service.maxParticipants,

    bookedServiceId: bookedServiceId,
    date: representativeBooking.bookingDate,
    startTime: representativeBooking.startTime,
    endTime: representativeBooking.endTime,
    displayTimeRange: displayTimeRange,
    status: status,

    rateId: rateId,
    durationMinutes: representativeBooking.rateDurationMinutes,
    rateAmount: representativeBooking.rateAmount,

    instructorId: instructor?.id ?? representativeBooking.instructorId ?? 0,
    instructorName: instructor?.name ?? "Unknown Instructor",

    participants: matchingBookings.map(
      (booking: Booking): ParticipantBasicInfo => ({
        id: booking.participant.email,
        bookingId: booking.id,
        name: booking.participant.name,
        email: booking.participant.email,
        createdAt: booking.createdAt,
        paymentStatus:
          booking.paymentStatus as AdminServiceParticipantPaymentStatus | null,
        paymentMethod: booking.paymentMethod,
        canceledAt: booking.cancelledAt,
        canceledBy: booking.cancelledAt
          ? booking.canceledByParticipant
            ? BookingCancellationSource.PARTICIPANT
            : BookingCancellationSource.STAFF
          : null,
      })
    ),
  };

  return detail;
}

/**
 * Formats a duration given in minutes into a display string.
 * - Shows minutes if 90 or less.
 * - Shows hours (including half hours like 1.5, 2.5) if duration is over 90 minutes
 *   and represents a whole or half hour.
 * - Falls back to minutes if over 90 but not a whole or half hour (e.g., 100 min).
 * @param durationMinutes The duration in total minutes.
 * @returns A formatted string like "60 min", "1.5 hrs", "2 hrs". Returns empty string if duration is invalid.
 */
export function formatDuration(
  durationMinutes: number | undefined | null
): string {
  if (typeof durationMinutes !== "number" || durationMinutes <= 0) {
    return "";
  }

  if (durationMinutes <= 90) {
    return `${durationMinutes} min`;
  }

  // Calculate hours (can be decimal)
  const hours = durationMinutes / 60;

  // Check if it's a whole or half hour (e.g., 2.0, 2.5)
  // Multiply by 2 and check if the result is an integer
  const isWholeOrHalfHour = Number.isInteger(hours * 2);

  if (isWholeOrHalfHour) {
    // Use 'hr' for exactly 1 hour, 'hrs' otherwise
    return `${hours} ${hours === 1 ? "hr" : "hrs"}`;
  }

  // Fallback: If > 90 but not a clean whole/half hour multiple
  return `${durationMinutes} min`;
}

/**
 * Formats a rate amount and duration into a standard display string.
 * Example: "$80.00 / 60 min", "$45.00 / 1.5 hrs"
 * @param amount The monetary amount of the rate.
 * @param durationMinutes The duration in total minutes.
 * @returns A formatted string combining currency and duration, or just currency if duration is invalid.
 */
export function formatRateDisplay(
  amount: number | undefined | null,
  durationMinutes: number | undefined | null
): string {
  const formattedAmount =
    typeof amount === "number" ? getFormattedCurrency(amount) : "$--.--"; // Use currency helper or fallback
  const formattedDur = formatDuration(durationMinutes);

  if (!formattedDur) {
    return formattedAmount; // Only show amount if duration is invalid/missing
  }

  return `${formattedAmount} / ${formattedDur}`;
}

/**
 * Determines the status of a session based on its time and cancellation status.
 * @param startTime ISO string for the start time.
 * @param endTime ISO string for the end time.
 * @param isCanceled Boolean indicating if the session is canceled.
 * @returns "Active", "Completed", or "Canceled".
 */
export const determineSessionStatus = (
  startTime: string,
  endTime: string,
  isCanceled: boolean
): "Active" | "Completed" | "Canceled" => {
  try {
    const end = parseISO(endTime);
    if (isCanceled) {
      return "Canceled";
    }
    if (isPast(end)) {
      return "Completed";
    }
    return "Active";
  } catch (e) {
    console.error("Error determining session status:", e, {
      startTime,
      endTime,
      isCanceled,
    });
    // Default to Active on error, or consider throwing
    return "Active";
  }
};

/**
 * Formats a time range for display in a compact, design-approved style.
 *
 * Rules (MAI-4696):
 * - Drop the minutes when the time lands on the hour ("9" not "9:00").
 * - Only show am/pm on the start time when it differs from the end's meridiem;
 *   otherwise the shared meridiem is shown once on the end time.
 *
 * Examples: "9 – 10am", "4:30 – 6pm", "5 – 5:45pm", "11am – 12pm".
 * Assumes input ISO strings are in the desired timezone or UTC.
 * @param startTime ISO string for the start time.
 * @param endTime ISO string for the end time.
 * @returns Formatted string like "4:30 – 6pm".
 */
export const formatTimeRangeForDisplay = (
  startTime: string,
  endTime: string,
  orgTimezone?: string
): string => {
  try {
    const start = dayjs(startTime).tz(orgTimezone);
    const end = dayjs(endTime).tz(orgTimezone);

    const formatPart = (value: dayjs.Dayjs, withMeridiem: boolean): string => {
      const time =
        value.minute() === 0 ? value.format("h") : value.format("h:mm");
      return withMeridiem ? `${time}${value.format("a").toLowerCase()}` : time;
    };

    const sameMeridiem =
      start.format("a").toLowerCase() === end.format("a").toLowerCase();
    const startFormat = formatPart(start, !sameMeridiem);
    const endFormat = formatPart(end, true);
    return `${startFormat} – ${endFormat}`;
  } catch (e) {
    console.error("Error formatting time range:", e, {
      startTime,
      endTime,
      orgTimezone,
    });
    return "Invalid time"; // Fallback on error
  }
};

/**
 * Formats a time input to the HH:mm format.
 * @param timeInput The time input to format.
 * @returns The formatted time string in HH:mm format.
 */
export const formatTimeToHHMM = (
  timeInput: string | Date | null | undefined
): string => {
  if (timeInput === null || timeInput === undefined) {
    return "00:00";
  }

  if (typeof timeInput === "string") {
    if (/^\d{2}:\d{2}$/.test(timeInput)) {
      try {
        const [hours, minutes] = timeInput.split(":").map(Number);
        if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
          return timeInput;
        } else {
          console.warn(
            `formatTimeToHHMM: Input string '${timeInput}' has HH:mm format but invalid hour/minute values.`
          );
        }
      } catch (e) {
        console.warn(
          `formatTimeToHHMM: Error processing string '${timeInput}' despite HH:mm regex match.`,
          e
        );
      }
    }
  }

  try {
    return format(timeInput, "HH:mm");
  } catch (e) {
    console.error(
      `formatTimeToHHMM: Failed to format timeInput with date-fns:`,
      timeInput,
      e
    );
    return "00:00";
  }
};

export interface ParsedTimeInputResult {
  candidates: number[];
  isAmbiguous: boolean;
  isEmpty: boolean;
}

function normalizeMeridiemSuffix(suffix: string): "am" | "pm" {
  return suffix.toLowerCase().startsWith("a") ? "am" : "pm";
}

/**
 * Parse a loosely-typed time string into total minutes from midnight.
 * Handles: "HH:mm" (24h), "h:mm am/pm", "ham/pm", and compact "HHmm" digits.
 * Returns null if the input cannot be parsed.
 */
export function parseTimeInputCandidates(input: string): ParsedTimeInputResult {
  const trimmed = input.trim().toLowerCase();

  if (!trimmed) {
    return {
      candidates: [],
      isAmbiguous: false,
      isEmpty: true,
    };
  }

  const unique = (
    candidates: number[],
    isAmbiguous = false
  ): ParsedTimeInputResult => ({
    candidates: [...new Set(candidates)].filter(
      (minutes) => minutes >= 0 && minutes < 24 * 60
    ),
    isAmbiguous,
    isEmpty: false,
  });

  const withAmPm = trimmed.match(/^(\d{1,2}):(\d{2})\s*(a|am|p|pm)$/i);
  if (withAmPm) {
    let hour = parseInt(withAmPm[1]!, 10);
    const minute = parseInt(withAmPm[2]!, 10);
    const period = normalizeMeridiemSuffix(withAmPm[3]!);

    if (minute < 0 || minute > 59 || hour < 1 || hour > 12) {
      return unique([]);
    }

    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;

    return unique([hour * 60 + minute]);
  }

  const compactWithAmPm = trimmed.match(/^(\d{3,4})\s*(a|am|p|pm)$/i);
  if (compactWithAmPm) {
    const numericValue = parseInt(compactWithAmPm[1]!, 10);
    let hour = Math.floor(numericValue / 100);
    const minute = numericValue % 100;
    const period = normalizeMeridiemSuffix(compactWithAmPm[2]!);

    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) {
      return unique([]);
    }

    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;

    return unique([hour * 60 + minute]);
  }

  const hourOnlyAmPm = trimmed.match(/^(\d{1,2})\s*(a|am|p|pm)$/i);
  if (hourOnlyAmPm) {
    let hour = parseInt(hourOnlyAmPm[1]!, 10);
    const period = normalizeMeridiemSuffix(hourOnlyAmPm[2]!);

    if (hour < 1 || hour > 12) {
      return unique([]);
    }

    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;

    return unique([hour * 60]);
  }

  const exact24Hour = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (exact24Hour) {
    const hour = parseInt(exact24Hour[1]!, 10);
    const minute = parseInt(exact24Hour[2]!, 10);

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return unique([]);
    }

    if (hour >= 13 || hour === 0 || hour === 12) {
      return unique([hour * 60 + minute]);
    }

    return unique([hour * 60 + minute, (hour + 12) * 60 + minute], true);
  }

  const compact = trimmed.match(/^(\d{1,4})$/);
  if (compact) {
    const digits = compact[1]!;

    if (digits.length <= 2) {
      const hour = parseInt(digits, 10);

      if (hour < 0 || hour > 23) {
        return unique([]);
      }

      if (hour >= 13 || hour === 0 || hour === 12) {
        return unique([hour * 60]);
      }

      return unique([hour * 60, (hour + 12) * 60], true);
    }

    const numericValue = parseInt(digits, 10);
    const hour = Math.floor(numericValue / 100);
    const minute = numericValue % 100;

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      return unique([]);
    }

    if (hour >= 13 || hour === 0 || hour === 12) {
      return unique([hour * 60 + minute]);
    }

    return unique([hour * 60 + minute, (hour + 12) * 60 + minute], true);
  }

  return unique([]);
}

export function parseTimeInputToMinutes(input: string): number | null {
  return parseTimeInputCandidates(input).candidates[0] ?? null;
}

/**
 * Round total minutes up to the nearest 15-minute boundary and return "HH:mm".
 * Caps at 23:45.
 */
export function roundUpToNearest15(totalMinutes: number): string {
  const rounded = Math.min(Math.ceil(totalMinutes / 15) * 15, 23 * 60 + 45);
  const h = Math.floor(rounded / 60)
    .toString()
    .padStart(2, "0");
  const m = (rounded % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

export const NULL_INSTRUCTOR_UUID = "00000000-0000-0000-0000-000000000000";

/** "14:15" -> "2:15 PM". Non-time input is returned unchanged. */
export function formatTimeLabel(time24h: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(time24h);
  if (!match) return time24h;
  const h = Number(match[1]);
  const m = match[2];
  if (h > 23) return time24h;
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:${m} ${h < 12 ? "AM" : "PM"}`;
}

/**
 * Collect the set of allowed class start times (HH:mm) from availability data.
 *
 * Returns null for non-class services so the caller shows every 15-minute
 * grid slot. For class services, always returns a Set — possibly empty —
 * so that a selected date with no availability yields an empty dropdown
 * rather than falling back to every grid slot (MAI-4452).
 */
interface ClassAllowedTimesSlot {
  readonly startTime: string;
  readonly isAvailable: boolean;
}
interface ClassAllowedTimesDay {
  readonly slots: readonly ClassAllowedTimesSlot[];
}

export function computeClassAllowedTimes(
  isClassService: boolean,
  availabilityData: readonly ClassAllowedTimesDay[] | null | undefined
): Set<string> | null {
  if (!isClassService) return null;
  const times = new Set<string>();
  for (const day of availabilityData ?? []) {
    for (const slot of day.slots) {
      if (slot.isAvailable) {
        times.add(slot.startTime.substring(0, 5));
      }
    }
  }
  return times;
}

/**
 * Generates an array of weekly dates starting from a base date.
 * Each subsequent date is 7 days after the previous one.
 * @param baseDate ISO date string (YYYY-MM-DD) for the first session.
 * @param count Number of weekly sessions to generate.
 * @returns Array of ISO date strings (YYYY-MM-DD).
 */
export function generateWeeklyDates(baseDate: string, count: number): string[] {
  const dates: string[] = [];
  const base = dayjs(baseDate);
  for (let i = 0; i < count; i++) {
    dates.push(base.add(i * 7, "day").format("YYYY-MM-DD"));
  }
  return dates;
}

/**
 * Returns the full day name for a given date string.
 * @param dateStr ISO date string (YYYY-MM-DD).
 * @returns Day name, e.g. "Monday", "Tuesday".
 */
export function getDayNameFromDate(dateStr: string): string {
  const dayIndex = dayjs(dateStr).day();
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayNames[dayIndex] ?? "";
}

/**
 * Formats a date string for display in the booking drawer.
 * @param dateStr ISO date string (YYYY-MM-DD).
 * @param timeStr Time in "h:mm A" format (e.g., "2:00 PM").
 * @returns Formatted string, e.g. "Mon, Feb 21 at 2:00pm".
 */
export function formatSessionDateDisplay(
  dateStr: string,
  timeStr: string
): string {
  const d = dayjs(dateStr);
  const dayAbbr = d.format("ddd");
  const monthDay = d.format("MMM D");
  return `${dayAbbr}, ${monthDay} at ${timeStr.toLowerCase()}`;
}

/**
 * Constructs a booking session composite ID from its components, converting the
 * local date+time in the org's timezone to a UTC Unix timestamp.
 *
 * Format: `{serviceId}__{rateId}__{instructorId}__{unixTimestamp}`
 */
export function buildCompositeId(
  serviceId: string,
  rateId: string,
  instructorId: string | null,
  date: string,
  time: string,
  orgTimezone: string
): string {
  const unix = dayjs.tz(`${date} ${time}`, orgTimezone).unix();
  return `${serviceId}__${rateId}__${instructorId ?? NULL_INSTRUCTOR_UUID}__${unix}`;
}

/** Start time (HH:mm, org timezone) for pre-filling the create-booking flow. */
export function getPresetStartTime(detail: BookedServiceDetailModel): string {
  const timezoneName = detail.displayTimeZone || "UTC";
  const parsedStart = dayjs.utc(detail.startTime);

  if (parsedStart.isValid()) {
    return parsedStart.tz(timezoneName).format("HH:mm");
  }

  return detail.startTime.slice(0, 5);
}

/**
 * Builds the CreateBookingDrawer preset for adding a participant to an
 * existing class session.
 */
export function buildAddParticipantPreset(
  detail: BookedServiceDetailModel,
  bookedServiceId: string
): CreateBookingPreset {
  return {
    serviceId: detail.serviceId,
    serviceTitle: detail.serviceTitle,
    rateId: detail.rateId,
    durationMinutes: detail.durationMinutes,
    rateAmount: detail.rateAmount,
    instructorId: detail.instructorId,
    instructorName: detail.instructorName,
    facilityAssets: detail.facilityAssets,
    date: detail.date,
    startTime: getPresetStartTime(detail),
    timeSlotId: bookedServiceId,
  };
}

type ServiceCapacityFields = Pick<Service, "serviceType" | "isGroupService">;

/**
 * Whether a service carries a participant capacity: group services and every
 * lesson (lessons default to a capacity of 1).
 */
export function serviceHasCapacity(service: ServiceCapacityFields): boolean {
  return (
    service.isGroupService || service.serviceType === ServiceTypeEnum.lesson
  );
}

/** Capacity to send to the API: the service's own value for capacity-bearing services, else 1. */
export function resolveMaxParticipantsForSave(
  service: ServiceCapacityFields & Pick<Service, "maxParticipants">
): number {
  return serviceHasCapacity(service) ? service.maxParticipants ?? 1 : 1;
}

/** Clamps a capacity input to a whole number between 1 and 100; empty stays empty. */
export function normalizeMaxParticipants(
  maxParticipants: number | null | undefined
): number | null {
  if (maxParticipants == null || !Number.isFinite(maxParticipants)) {
    return null;
  }
  return Math.min(100, Math.max(1, Math.trunc(maxParticipants)));
}
